import React, { useState, useEffect, useRef } from "react";
import "./Tabs.css";
import { Link } from "react-router-dom";

const PodcastIcon = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12 3a9 9 0 0 0-9 9 1 1 0 1 0 2 0 7 7 0 1 1 14 0 1 1 0 1 0 2 0 9 9 0 0 0-9-9Zm0 4a5 5 0 0 0-5 5 1 1 0 1 0 2 0 3 3 0 0 1 6 0 1 1 0 1 0 2 0 5 5 0 0 0-5-5Zm0 4a1 1 0 0 0-1 1v6a1 1 0 1 0 2 0v-6a1 1 0 0 0-1-1Z"/>
  </svg>
);

const WebinarIcon = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M3 4a1 1 0 0 0-1 1v11a2 2 0 0 0 2 2h7v2H8a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2h-3v-2h7a2 2 0 0 0 2-2V5a1 1 0 0 0-1-1H3Zm1 2h16v10H4V6Zm5 1v8l6-4-6-4Z"/>
  </svg>
);

const EventIcon = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M6 2a1 1 0 1 0 0 2H7v1a1 1 0 1 0 2 0V4h6v1a1 1 0 1 0 2 0V4h1a1 1 0 1 0 0-2H6ZM3 7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v13a2 2 0 0 1-2 2H4a2 2 0 0 1-1-2V7Zm2 1v12h14V8H5Zm3 3h8v2H8v-2Z"/>
  </svg>
);

const CourseIcon = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M4 4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h3v13a1 1 0 0 0 1.447.894l5-2.5a1 1 0 0 0 0-1.788L8.447 13H20a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H4Z"/>
  </svg>
);

const TABS = [
  {
    id: "one",
    icon: <PodcastIcon className="tab-icon" />,
    labelTop: "PODCAST",
    heading: "Actionable insights from global trainers and entrepreneurs",
    leadItalic: "Conversations on growth, leadership, and building resilient businesses.",
    bullets: [
      "New episodes every week with industry experts",
      "Real stories, practical frameworks, zero fluff",
      "Available on YouTube",
    ],
    after: "Start listening and level up your mindset in 20 minutes a week.",
    image: "assets/img/tabs/podcast.jpg",
    button: { text: "🎙️ Listen to Our Podcasts", link: "/podcast" },
  },
  {
    id: "two",
    icon: <WebinarIcon className="tab-icon" />,
    labelTop: "WEBINAR",
    heading: "Live, interactive trainings with Q&A and implementation",
    leadItalic: "Hands-on sessions designed to help you execute, not just learn.",
    bullets: [
      "Live Q&A with mentors and coaches",
      "Worksheets, templates, and replay access",
      "Topic-focused sessions every month",
    ],
    after: "Reserve your seat and get reminders so you never miss a session.",
    image: "assets/img/tabs/webinar.jpg",
    button: { text: "🎥 Join Our Webinars", link: "/webinar" },
  },
  {
    id: "three",
    icon: <CourseIcon className="tab-icon" />,
    labelTop: "COURSES",
    heading: "Structured courses to master skills at your own pace",
    leadItalic: "Step-by-step video lessons with community, assignments, and certificates.",
    bullets: [
      "Beginner to advanced tracks with clear outcomes",
      "Mentor feedback and peer support",
      "Lifetime updates and downloadable resources",
    ],
    after: "Browse the catalog and start your learning path today.",
    image: "assets/img/tabs/course.jpg",
    button: { text: "📚 Browse Courses", link: "/courses" },
  },
  {
    id: "four",
    icon: <EventIcon className="tab-icon" />,
    labelTop: "EVENTS",
    heading: "High-energy events that connect, inspire, and transform",
    leadItalic: "From summits to masterclasses — meet peers, mentors, and collaborators.",
    bullets: [
      "Network with 200+ leaders and creators",
      "Expert keynotes and real business case studies",
      "On-ground and virtual experiences across cities",
    ],
    after: "Check the calendar and book your slot for the next big event.",
    image: "assets/img/tabs/event.jpg",
    button: { text: "📅 Explore Events", link: "/events" },
  },
];

export default function TabbedFeatures() {
  const [active, setActive] = useState(TABS[0].id);
  const rowRef = useRef(null);
  const underlineRef = useRef(null);

  // ✅ Load last active tab from localStorage on mount
  useEffect(() => {
    const savedTab = localStorage.getItem("activeTab");
    if (savedTab && TABS.some((t) => t.id === savedTab)) {
      setActive(savedTab);
    }
  }, []);

  // ✅ Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("activeTab", active);
  }, [active]);

  // ✅ Animate underline movement
  useEffect(() => {
    const row = rowRef.current;
    const underline = underlineRef.current;
    if (!row || !underline) return;

    const activeBtn = row.querySelector(".tab-chip.active");
    if (activeBtn) {
      const { offsetLeft, offsetWidth } = activeBtn;
      underline.style.width = `${offsetWidth - 40}px`;
      underline.style.left = `${offsetLeft + 20}px`;
    }
  }, [active]);

  const current = TABS.find((t) => t.id === active);

  return (
    <section className="presento-tabs">
      <div
        className="tabs-row"
        role="tablist"
        aria-label="Features"
        ref={rowRef}
      >
        {TABS.map((t) => {
          const isActive = t.id === active;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${t.id}`}
              id={`tab-${t.id}`}
              tabIndex={isActive ? 0 : -1}
              className={`tab-chip ${isActive ? "active" : ""}`}
              onClick={() => setActive(t.id)}
            >
              {t.icon}
              <div className="tab-text">
                <span>{t.labelTop}</span>
              </div>
            </button>
          );
        })}
        <div className="tab-underline" ref={underlineRef}></div>
      </div>

      <div
        id={`panel-${current.id}`}
        role="tabpanel"
        aria-labelledby={`tab-${current.id}`}
        className="tab-panel"
      >
        <div className="panel-left">
          <h2 className="panel-heading">{current.heading}</h2>
          <p className="panel-lead">
            <em>{current.leadItalic}</em>
          </p>

          <ul className="tick-list">
            {current.bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>

          <p className="panel-after">{current.after}</p>

          <div className="tab-button" style={{ marginTop: "30px" }}>
            <Link to={current.button.link} className="btn-get-started">
              {current.button.text}
            </Link>
          </div>
        </div>

        <div className="panel-right">
          <div className="feature-image">
            <img
              src={current.image}
              alt="Feature"
              className="feature-img"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
